/**
 * Domain model for a built-in closet.
 *
 * All linear measurements are in CENTIMETRES unless noted.
 * Metric defaults: sheet = 122 cm x 244 cm, carcass = 1.8 cm, back = 0.6 cm.
 *
 * Coordinate system (3D preview):
 *   +X = width (left -> right along the wall)
 *   +Y = height (floor -> ceiling)
 *   +Z = depth  (wall -> room)
 * Origin is the bottom-left-back corner of the closet's enclosing box.
 */

export type CeilingType = "flat" | "slanted" | "gable";

/** Vertical sub-volume of the closet between two side panels. */
export interface ClosetSection {
  id: string;
  /** Section width in centimetres (interior or exterior — see widthMode). */
  width: number;
  /** How many adjustable shelves to generate (excludes top/bottom). */
  shelfCount: number;
  /** If true, include a hanging rod centered in the section. */
  hangingRod: boolean;
  /** If true, include a 6-tall stack of drawers in the lower portion. */
  drawerStack: boolean;
}

export interface CeilingSpec {
  type: CeilingType;
  /**
   * Heights in centimetres.
   * For 'flat': height equals heightLeft (heightRight ignored).
   * For 'slanted': linear slope from heightLeft on the left side to heightRight on the right.
   * For 'gable': peak at center; eave heights both = heightLeft, peak height = heightRight.
   */
  heightLeft: number;
  heightRight: number;
}

export interface MaterialSpec {
  /** Carcass (sides/top/bottom/shelves) thickness in cm, e.g. 1.8. */
  carcassThickness: number;
  /** Back panel thickness in cm, e.g. 0.6. */
  backThickness: number;
  /** Sheet width in cm (the SHORTER dim of the sheet). */
  sheetWidth: number;
  /** Sheet length in cm (the LONGER dim of the sheet). */
  sheetLength: number;
  /** CNC bit / saw kerf in cm; subtracted as separation between parts. */
  kerf: number;
}

export interface ClosetSpec {
  /** Total exterior width along the wall (sum of section widths + side panels + dividers). */
  totalWidth: number;
  /** Depth front-to-back. */
  depth: number;
  /** Toe kick height (set to 0 to disable). */
  toeKickHeight: number;
  ceiling: CeilingSpec;
  material: MaterialSpec;
  sections: ClosetSection[];
}

/** A flat rectangular part to be cut from a sheet. */
export interface Part {
  id: string;
  /** Human-readable label printed on the cut diagram. */
  label: string;
  /** Width (cm) — the dimension that should align with sheet width. */
  width: number;
  /** Length (cm) — the dimension that should align with sheet length / grain. */
  length: number;
  /** Thickness in cm; influences which sheet stock the part draws from. */
  thickness: number;
  /** True if this part has a non-rectangular top edge (slanted/gable cut). */
  angledTop?: boolean;
  /**
   * For angledTop parts: the SHORTER edge length opposite the long edge.
   * The cut becomes a trapezoid: [length x width] rect with the top trimmed
   * from `width` down to `shortEdge` along a straight line.
   */
  shortEdge?: number;
  /** Optional notes for the operator. */
  notes?: string;
}

export const DEFAULT_MATERIAL: MaterialSpec = {
  carcassThickness: 1.8,
  backThickness: 0.6,
  sheetWidth: 122,
  sheetLength: 244,
  kerf: 0.3,
};

export const DEFAULT_CLOSET: ClosetSpec = {
  totalWidth: 240,
  depth: 60,
  toeKickHeight: 10,
  ceiling: { type: "flat", heightLeft: 244, heightRight: 244 },
  material: DEFAULT_MATERIAL,
  sections: [
    {
      id: "s1",
      width: 75,
      shelfCount: 4,
      hangingRod: true,
      drawerStack: false,
    },
    { id: "s2", width: 75, shelfCount: 0, hangingRod: true, drawerStack: true },
    {
      id: "s3",
      width: 76.4,
      shelfCount: 5,
      hangingRod: false,
      drawerStack: false,
    },
  ],
};
